/**
 * Сжатие фотографии на устройстве перед отправкой.
 *
 * Снимок с телефона — это 8–12 мегапикселей и 4–8 МБ. Telegram всё равно
 * покажет его максимум в ~1280px по длинной стороне, так что отправлять
 * оригинал бессмысленно: через туннель это десятки секунд и упор в лимит
 * Bot API (10 МБ на фото). После сжатия тот же кадр весит 200–400 КБ и
 * уходит почти мгновенно.
 *
 * Видео не трогаем — сжать его в браузере нельзя.
 */

const MAX_SIDE = 1600;
const QUALITY = 0.85;
/** Мелкие файлы трогать незачем — перекодирование только испортит. */
const SKIP_BELOW_BYTES = 400 * 1024;

export type CompressResult = {
  file: File;
  /** Сжали или отдали оригинал как есть. */
  compressed: boolean;
  originalBytes: number;
};

export async function compressImage(file: File): Promise<CompressResult> {
  const originalBytes = file.size;

  if (!file.type.startsWith('image/') || file.size <= SKIP_BELOW_BYTES) {
    return { file, compressed: false, originalBytes };
  }

  try {
    const bitmap = await loadBitmap(file);
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { file, compressed: false, originalBytes };
    ctx.drawImage(bitmap, 0, 0, width, height);
    if ('close' in bitmap) bitmap.close();

    const blob = await toBlob(canvas);
    // Если «сжатие» вышло тяжелее оригинала — отправляем оригинал.
    if (!blob || blob.size >= file.size) {
      return { file, compressed: false, originalBytes };
    }

    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return {
      file: new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }),
      compressed: true,
      originalBytes,
    };
  } catch {
    // HEIC без поддержки декодирования, нехватка памяти и прочее —
    // отправляем оригинал, сервер разберётся.
    return { file, compressed: false, originalBytes };
  }
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    // Телефонные снимки часто повёрнуты через EXIF — просим учесть ориентацию.
    return createImageBitmap(file, { imageOrientation: 'from-image' });
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('не удалось прочитать изображение'));
    };
    image.src = url;
  });
}

function toBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${Math.round(bytes / 1024)} КБ`;
}
